import FilterChip from 'LuxKit/Chips/FilterChip'
import Group from 'components/utils/Group'
import { useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import { FlightDealsCarouselFilterItem } from './FlightDealsCarouselFilterItem'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useToggle from 'hooks/useToggle'
import { ALL_KEY, FilterType } from '../flightDealsFilter.constants'
import type { FlightsClassTypes } from 'constants/flight'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'

const FiltersContainer = styled(Group)`
  padding: 0 ${rem(16)};
`

interface Filter {
  label: string
  value: string
  price?: number
}

interface Props {
  airlines: Array<Filter>
  cabins: Array<Filter>
  selectedAirlines: Set<string>
  selectedCabins: Set<string>
  handleToggle: (type: FilterType, value: string) => void
}

export function FlightDealsCarouselStaticFilterPills(props: Props) {
  const { airlines, cabins, selectedAirlines, selectedCabins, handleToggle } = props
  const airlinesDropdownTrigger = useRef<HTMLButtonElement>(null)
  const cabinsDropdownTrigger = useRef<HTMLButtonElement>(null)
  const { value: showAirlinesFilter, toggle: toggleAirlinesFilter } = useToggle()
  const { value: showCabinsFilter, toggle: toggleCabinsFilter } = useToggle()

  const intl = useIntl()
  const { fareCabinLabelMap } = useFlightFareLabels()

  const showAirlinesNum = selectedAirlines.size > 0 && !selectedAirlines.has(ALL_KEY)
  const showCabinsNum = selectedCabins.size > 0 && !selectedCabins.has(ALL_KEY)

  const allAirlinesLabel = intl.formatMessage({
    id: 'I17IPDY0XWplkczGn2Ox',
    defaultMessage: 'All airlines',
  })
  const airlinesLabel = intl.formatMessage({
    id: 'xpA-qN2EcFb7PkfYBnXi',
    defaultMessage: 'Airlines',
  })
  const allCabinsLabel = intl.formatMessage({
    id: 'BxyetAuz5Vzabjq0j6Yr',
    defaultMessage: 'All cabins',
  })
  const cabinsLabel = intl.formatMessage({
    id: 'x8c1gNL2ryyzuNrTkYUf',
    defaultMessage: 'Cabins',
  })

  return (
    <>
      <FilterChip
        size="medium"
        ref={airlinesDropdownTrigger}
        onClick={toggleAirlinesFilter}
        endIcon={showAirlinesFilter ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
      >
        {!showAirlinesNum ? allAirlinesLabel : airlinesLabel} {showAirlinesNum && intl.formatMessage(
          {
            id: 'RVjuI2XrzVD-7R4N2BGd',
            defaultMessage: '({count, number})',
          },
          { count: selectedAirlines.size },
        )}
      </FilterChip>

      <FilterChip
        size="medium"
        ref={cabinsDropdownTrigger}
        endIcon={showCabinsFilter ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
        onClick={toggleCabinsFilter}
      >
        {!showCabinsNum ? allCabinsLabel : cabinsLabel} {showCabinsNum && intl.formatMessage(
          {
            id: 'RVjuI2XrzVD-7R4N2BGd',
            defaultMessage: '({count, number})',
          },
          { count: selectedCabins.size },
        )}
      </FilterChip>

      <DropdownList
        size="M"
        open={showAirlinesFilter}
        triggerRef={airlinesDropdownTrigger}
        anchorRef={airlinesDropdownTrigger}
        onClose={toggleAirlinesFilter}
        placement="bottom-start"
        portaled
      >
        <FiltersContainer direction="vertical">
          <VerticalSpacer gap={16}>
            <FlightDealsCarouselFilterItem
              checked={selectedAirlines.has(ALL_KEY)}
              onCheckedChange={() => handleToggle(FilterType.AIRLINES, ALL_KEY)}
              label={intl.formatMessage({
                id: 'Pj41CERbOLnCYVRjWxF5',
                defaultMessage: 'All airlines',
              })}
            />

            {airlines.map(airline => (
              <FlightDealsCarouselFilterItem
                key={airline.value}
                checked={selectedAirlines.has(airline.value)}
                onCheckedChange={() => handleToggle(FilterType.AIRLINES, airline.value)}
                label={airline.label}
                price={airline.price}
              />
            ))}
          </VerticalSpacer>
        </FiltersContainer>
      </DropdownList>

      <DropdownList
        size="M"
        open={showCabinsFilter}
        triggerRef={cabinsDropdownTrigger}
        anchorRef={cabinsDropdownTrigger}
        onClose={toggleCabinsFilter}
        placement="bottom-start"
        portaled
      >
        <FiltersContainer direction="vertical">
          <VerticalSpacer gap={16}>
            <FlightDealsCarouselFilterItem
              checked={selectedCabins.has(ALL_KEY)}
              onCheckedChange={() => handleToggle(FilterType.CABINS, ALL_KEY)}
              label={intl.formatMessage({
                id: 'Sn5M0ooomOmSKOwQGxQr',
                defaultMessage: 'All cabins',
              })}
            />

            {cabins.map(cabin => (
              <FlightDealsCarouselFilterItem
                key={cabin.value}
                checked={selectedCabins.has(cabin.value)}
                onCheckedChange={() => handleToggle(FilterType.CABINS, cabin.value)}
                label={fareCabinLabelMap[cabin.value as FlightsClassTypes]}
                price={cabin.price}
              />
            ))}
          </VerticalSpacer>
        </FiltersContainer>
      </DropdownList>
    </>
  )
}
